"use client";

import React from "react";
import { ShieldCheck, Award, ArrowRight, CircleCheck as CheckCircle2, HardHat, Building2, House as Home } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyPoints = [{
  title: "Commercial & residential scope",
  description: "From residential chimney stacks and roof repairs to multi-storey developments and commercial sites.",
  icon: Building2
}, {
  title: "NASC & CHAS accredited",
  description: "Fully compliant safety management, verified site procedures, and rigorous inspection protocols.",
  icon: ShieldCheck
}, {
  title: "20+ years directed experience",
  description: "Led by Pete Wood with two decades of hands-on scaffolding mastery across Berkshire.",
  icon: HardHat
}];
export default function CompanyIntro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="companyintro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main narrative column */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold border-border px-3 py-1">
                Reading &amp; Berkshire Scaffolding
              </Badge>
              <Badge variant="secondary" className="text-xs font-medium px-3 py-1 flex items-center gap-1.5">
                <Award className="size-3.5 text-primary" />
                <span>NASC &amp; CHAS Accredited</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
              Scaffolding in Reading
            </motion.h1>

            <motion.h2 variants={itemVariants} className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight -mt-2">
              Safe, practical scaffolding across Reading and Berkshire
            </motion.h2>

            <motion.div variants={itemVariants} className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="text-foreground font-medium">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Reading and the surrounding Berkshire area, including RG1, RG2, RG4, RG30 and RG31.
              </p>
              <p>
                We work alongside homeowners, roofing contractors, housing developers, property managers, and commercial project leaders to supply safe, well-planned working platforms tailored to each build.
              </p>
              <p>
                Founded on more than 20 years of hands-on industry experience under Pete Wood, our work is defined by exacting technical standards, clear communication, and uncompromised site safety. With confirmed NASC and CHAS accreditations, we ensure every structure meets rigorous safety regulations from initial design to prompt dismantle.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground">
                <Link to="/AboutBmfPage">Learn about BMF</Link>
              </Button>
            </motion.div>
          </div>

          {/* Right feature cards */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <motion.div variants={itemVariants}>
              <Card className="border border-border bg-card shadow-xs">
                <CardContent className="p-6 sm:p-7 space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                      Operational overview
                    </span>
                    <span className="inline-flex items-center text-xs font-semibold text-foreground bg-muted px-2.5 py-1 rounded-md">
                      20+ Years Trade Experience
                    </span>
                  </div>

                  <Separator className="bg-border" />

                  <div className="space-y-5">
                    {keyPoints.map((point, index) => {
                    const IconComponent = point.icon;
                    return <div key={point.title} data-index={index} className="flex items-start gap-4">
                          <div className="shrink-0 rounded-md bg-accent p-2.5 text-primary border border-border">
                            <IconComponent className="size-5" />
                          </div>
                          <div className="space-y-1">
                            <h3 className="font-default font-semibold text-sm sm:text-base text-foreground">
                              {point.title}
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                              {point.description}
                            </p>
                          </div>
                        </div>;
                  })}
                  </div>

                  <Separator className="bg-border" />

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <Link to="/CommercialScaffoldingPage" className="group flex flex-col p-3 rounded-md bg-muted/60 hover:bg-muted border border-border transition-[color,background-color] text-foreground">
                      <span className="text-xs font-semibold flex items-center justify-between">
                        Commercial
                        <ArrowRight className="size-3 text-muted-foreground group-hover:text-primary transition-colors" />
                      </span>
                      <span className="text-xs text-muted-foreground mt-1">Sites &amp; developers</span>
                    </Link>

                    <Link to="/ResidentialScaffoldingPage" className="group flex flex-col p-3 rounded-md bg-muted/60 hover:bg-muted border border-border transition-[color,background-color] text-foreground">
                      <span className="text-xs font-semibold flex items-center justify-between">
                        Residential
                        <ArrowRight className="size-3 text-muted-foreground group-hover:text-primary transition-colors" />
                      </span>
                      <span className="text-xs text-muted-foreground mt-1">Homes &amp; roofing</span>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}